import { useEffect, useState } from "react";
import { useRoute } from "wouter";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import NotFound from "@/pages/not-found";
import { PageRenderer } from "@/components/page-renderer";
import { SiteHero } from "@/components/sections/site-hero";
import { LocationPage } from "@/components/location/location-page";
import { isLocationCtx } from "@/components/location/location-data";

type Page = {
  id?: number; title: string; slug: string;
  blocks: any[]; seoTitle: string | null; metaDescription: string | null;
  focusKeyphrase: string | null; keywords: string[] | null;
  featuredImageUrl: string | null; ogImageUrl: string | null;
  canonicalUrl: string | null; noindex: boolean;
  schemas?: any[] | null;
  context?: Record<string, any>;
};

function setMeta(name: string, content: string, attr: "name" | "property" = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

const SCHEMA_TAG_ID = "vertex-jsonld";
function setJsonLd(items: any[]) {
  document.querySelectorAll(`script[data-id="${SCHEMA_TAG_ID}"]`).forEach(n => n.remove());
  for (const item of items) {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-id", SCHEMA_TAG_ID);
    s.text = JSON.stringify(item).replace(/</g, "\\u003c").replace(/>/g, "\\u003e");
    document.head.appendChild(s);
  }
}

function buildSchemas(page: Page): any[] {
  const out: any[] = [];
  const url = typeof window !== "undefined" ? window.location.href : "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  for (const s of (page.schemas || [])) {
    if (!s || !s.type) continue;
    if (s.type === "faq") {
      const items = (s.items || page.blocks.filter(b => b.type === "faq").flatMap((b: any) => b.items || []));
      if (items.length) out.push({
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: items.map((q: any) => ({ "@type": "Question", name: q.question, acceptedAnswer: { "@type": "Answer", text: q.answer } })),
      });
    } else if (s.type === "localBusiness") {
      out.push({
        "@context": "https://schema.org", "@type": s.businessType || "LocalBusiness",
        name: s.name, image: s.image, telephone: s.telephone, email: s.email, url: s.url || url, priceRange: s.priceRange,
        address: s.address ? { "@type": "PostalAddress", ...s.address } : undefined,
        areaServed: s.areaServed, openingHours: s.openingHours,
      });
    } else if (s.type === "service") {
      out.push({
        "@context": "https://schema.org", "@type": "Service",
        name: s.name || page.seoTitle || page.title, serviceType: s.serviceType,
        description: s.description || page.metaDescription,
        provider: { "@type": "LocalBusiness", name: s.providerName || "VertexAir Climate Systems" },
        areaServed: s.areaServed, url,
      });
    } else if (s.type === "article") {
      out.push({
        "@context": "https://schema.org", "@type": "Article",
        headline: page.seoTitle || page.title,
        description: page.metaDescription, image: page.featuredImageUrl,
        author: { "@type": "Organization", name: "VertexAir Climate Systems" },
        publisher: { "@type": "Organization", name: "VertexAir Climate Systems" },
        mainEntityOfPage: url,
      });
    } else if (s.type === "breadcrumb") {
      const crumbs = s.items && s.items.length ? s.items : [{ name: "Home", url: origin + "/" }, { name: page.title, url }];
      out.push({
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: crumbs.map((c: any, i: number) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: c.url })),
      });
    }
  }
  return out;
}

export default function DynamicPage() {
  const [, p1] = useRoute("/:slug");
  const [, p2] = useRoute("/:parent/:slug");
  const slug = (p2 as any)?.slug || (p1 as any)?.slug || "";
  const fullSlug = (p2 as any) ? `${(p2 as any).parent}/${(p2 as any).slug}` : slug;

  const ssrInitial = typeof window !== "undefined" ? (window as any).__SSR_DATA__ : null;
  const ssrMatches = ssrInitial && (ssrInitial.slug === slug || ssrInitial.slug === fullSlug);
  const [page, setPage] = useState<Page | null>(ssrMatches ? ssrInitial : null);
  const [status, setStatus] = useState<"loading" | "ready" | "notfound">(ssrMatches ? "ready" : "loading");

  useEffect(() => {
    if (typeof window !== "undefined") {
      try { delete (window as any).__SSR_DATA__; } catch {}
    }
  }, []);

  useEffect(() => {
    if (page && (page.slug === slug || page.slug === fullSlug)) return;
    if (!slug) { setStatus("notfound"); return; }
    setStatus("loading"); setPage(null);
    const tries = [fullSlug, slug];
    const tryNext = async (i: number): Promise<void> => {
      if (i >= tries.length) { setStatus("notfound"); return; }
      try {
        const r = await fetch(`${import.meta.env.BASE_URL}api/pages/resolve/${encodeURIComponent(tries[i])}`);
        if (r.ok) {
          const { page } = await r.json();
          setPage(page); setStatus("ready"); return;
        }
      } catch {}
      tryNext(i + 1);
    };
    tryNext(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, fullSlug]);

  useEffect(() => {
    if (!page) return;
    document.title = page.seoTitle || page.title;
    setMeta("description", page.metaDescription || "");
    if (page.keywords && page.keywords.length) setMeta("keywords", page.keywords.join(", "));
    setMeta("robots", page.noindex ? "noindex, nofollow" : "index, follow");
    setMeta("og:title", page.seoTitle || page.title, "property");
    setMeta("og:description", page.metaDescription || "", "property");
    setMeta("og:type", "article", "property");
    if (page.ogImageUrl || page.featuredImageUrl) setMeta("og:image", page.ogImageUrl || page.featuredImageUrl || "", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", page.seoTitle || page.title);
    setMeta("twitter:description", page.metaDescription || "");
    if (page.ogImageUrl || page.featuredImageUrl) setMeta("twitter:image", page.ogImageUrl || page.featuredImageUrl || "");
    if (page.canonicalUrl) setLink("canonical", page.canonicalUrl);
    setJsonLd(buildSchemas(page));
  }, [page]);

  useEffect(() => () => setJsonLd([]), []);

  if (status === "loading") {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent" /></div>;
  }
  if (status === "notfound" || !page) return <NotFound />;

  const hasHero = page.blocks?.[0]?.type === "hero";
  const ctx: any = page.context || {};
  const service = ctx.service?.title as string | undefined;
  const city = ctx.city?.title as string | undefined;
  const heroTitle = service && city
    ? `${service} in ${city}`
    : page.title;
  const heroHighlight = service && city ? "| VertexAir" : undefined;
  const heroSubtitle = page.metaDescription || undefined;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {isLocationCtx(ctx) ? (
          <LocationPage ctx={ctx} blocks={page.blocks} fallbackTitle={page.title} />
        ) : (<>
        {!hasHero && (
          <SiteHero
            title={heroTitle}
            highlight={heroHighlight}
            subtitle={heroSubtitle}
            backgroundImageUrl={page.featuredImageUrl}
          />
        )}
        <article className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <PageRenderer blocks={page.blocks} context={page.context} />
        </article>
        </>)}
      </main>
      <Footer />
    </div>
  );
}
